import { useMemo } from 'react';
import {
  QueryContainer,
  RangeQuery,
} from '@opensearch-project/opensearch/api/_types/_common.query_dsl';

import { NumericListItem } from '../types';
import { useOpenSearchContext } from './useOpenSearchContext';
import { buildQueryFilterFromNumericItem } from '../utils';
import { useUpdateSearchParams } from './useUpdateSearchParams';

const useNumericMenu = ({ attribute, items }: Args) => {
  const { updateSearchParams } = useUpdateSearchParams();

  const { queryFiltersByFacets, updateQueryFiltersByFacets, aggregations } =
    useOpenSearchContext();

  const maxNumericValue = aggregations?.facets?.[`${attribute}_max`]?.value;

  const hasAttributeValue =
    !!aggregations?.facets?.[attribute]?.values?.buckets?.length;

  const currentFilterValue = useMemo(
    () =>
      (
        (queryFiltersByFacets[attribute] as QueryContainer)?.bool
          ?.filter as QueryContainer
      )?.range?.[attribute] as RangeQuery | undefined,
    [queryFiltersByFacets, attribute],
  );

  const numericItems: NumericListItem[] = items.map(
    ({ label, start, end }) => ({
      label,
      value: { start, end },
      isRefined:
        currentFilterValue?.gte === start && currentFilterValue?.lte === end,
    }),
  );

  const handleRefine = ({ start, end }: { start?: number; end?: number }) => {
    if (start || end) {
      updateSearchParams(attribute, [`${start ?? ''}:${end ?? ''}`]);
    } else {
      // If no start or end, clear the filter
      updateSearchParams(attribute, []);
    }

    updateQueryFiltersByFacets((prev: Record<string, RangeQuery>) => ({
      ...prev,
      [attribute]: buildQueryFilterFromNumericItem(attribute, { start, end }),
    }));
  };

  return {
    items: numericItems,
    refine: handleRefine,
    currentFilterValue,
    maxNumericValue,
    hasAttributeValue,
  };
};

type Args = {
  attribute: string;
  items: NumericItem[];
};

type NumericItem = {
  label: string;
  start?: number;
  end?: number;
};

export { useNumericMenu };
